import {
  MoreInfoOutlined,
} from '@duxton-web/icons';
import { Card, Flex, Form, Popover, Select } from '@duxton-web/ui';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getApprovalFlowConfig, getApprovers } from 'omega-rtc/actions/newTridentExtra';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';
import { showControlGroup } from 'omega-rtc/config';
import {
  HUMAN_READ_DATE_TIME_FORMAT,
  SMART_CAMPAIGN_FORM_KEYS,
} from 'omega-rtc/constants/ucd';
import { generatedCampaignCode } from 'omega-rtc/utils/smart-campaign-utils';
import { getVariantNumberFromSmartCommsVariantName } from 'omega-rtc/utils/ucd-utils';
import SmartCampaignOverviewCard from './SmartCampaignOverviewCard';
import { convertTimeWindowToHoursInUTC } from 'omega-rtc/utils/time-utils';

dayjs.extend(timezone);

const SmartCampaignPreview = () => {
  const projectView = useSelector((state: any) => state.ucd.projectView);
  const form = Form.useFormInstance();
  const {
    selectedLanguages,
    primaryLanguageContentsInForm,
    isCurrentContentSourceManual,
  } = useCreateSmartCampaign();
  const [formValuesState, setFormValuesState] = useState({
    campaignName: '',
    startDate: null,
    endDate: null,
    hoursInUTC: null,
    controlGroupTime: null,
    project: '',
    segments: '',
    vertical: '',
    approver: '',
    actionType: '',
    actionURL: '',
    channel: undefined,
    campaignData: undefined,
    pushNotificationImage: {},
    pciTitle: {},
    pciMessage: {},
    pciCategory: {},
    pciSubtitle: {},
    coverImageURL: {},
    pciButtonText: {},
    pciButtonLink: {},
    isRecurrenceEnabled: false,
    audienceType: '',
  });

  const newTridents = useSelector((state: any) => state.newTridents);
  const dispatch = useDispatch();

  const countries = useSelector((state: any) => state.countries.entities);

  const country = useMemo(() => {
    return (
      countries &&
      projectView &&
      projectView.project &&
      countries.find(
        (country: any) => country.countryID === projectView.project.countryID,
      )
    );
  }, [countries, projectView]);

  const controlGroupTime = useMemo(() => {
    return formValuesState.controlGroupTime &&
      country &&
      country.timezones &&
      country.timezones[0]
      ? dayjs(formValuesState.controlGroupTime)
        .tz(country.timezones[0].tz, true)
        .format(HUMAN_READ_DATE_TIME_FORMAT)
      : '';
  }, [country, formValuesState.controlGroupTime]);

  const campaignView = useSelector((state: any) => state.ucd.campaignView);
  const approvalFlowConfig = useSelector((state: any) => state.newTridents.approvalFlowConfig);
  const allowSelfApprove = _.get(approvalFlowConfig, 'allowSelfApprove', true);
  const user = useSelector((state: any) => state.app.user);
  const isSelfApprove = useMemo(() => {
    return _.includes(getPastEditors(campaignView), user.email);
  }, [campaignView, user]);

  const isRecurrenceEnabled = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN);
  const recurrenceWindowDays = form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS);

  const campaignCode = useMemo(() => {
    // campaignView.campaign.code will be undefined in smart campaign duplicate.
    if (campaignView && campaignView.campaign && campaignView.campaign.code) {
      return campaignView && campaignView.campaign.code;
    }

    const shortName = (formValuesState.campaignName || '').replace(/ /g, '_');
    return generatedCampaignCode(
      campaignView,
      shortName,
      [formValuesState.vertical],
      formValuesState.startDate,
      projectView,
      countries,
    );
  }, [
    campaignView,
    projectView,
    formValuesState.campaignName,
    formValuesState.vertical,
    formValuesState.startDate,
    countries,
  ]);

  useEffect(() => {
    dispatch(getApprovalFlowConfig());
    setFormValuesState({
      campaignName: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME),
      startDate: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.START_DATE),
      endDate: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.END_DATE),
      hoursInUTC: convertTimeWindowToHoursInUTC(form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW), country && country.timezones),
      controlGroupTime: form.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.CONTROL_GROUP_TIME,
      ) || (!showControlGroup && form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.START_DATE)
        ? dayjs(form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.START_DATE)).hour(11).minute(0).second(0)
        : null),
      segments: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.SEGMENTS),
      vertical: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL),
      approver: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.APPROVER),
      project: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PROJECT),
      campaignData: form.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
      ),
      actionType: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE),
      actionURL: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.ACTION_URL),
      channel: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL),
      pciTitle: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE),
      pciCategory: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY),
      pciSubtitle: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_SUBTITLE),
      coverImageURL: form.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL,
      ),
      pciMessage: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE),
      pciButtonText: form.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT,
      ),
      pciButtonLink: form.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_LINK,
      ),

      pushNotificationImage: form.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL,
      ),
      isRecurrenceEnabled: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN),
      audienceType: form.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.AUDIENCE_TYPE),
    });
  }, [form.getFieldValue, dispatch]);

  const metaDataForEachLanguages = useMemo(() => {
    return getContentVariantsAndPCIDataForEachLanguages(
      primaryLanguageContentsInForm,
      formValuesState.campaignData,
      selectedLanguages,
      formValuesState, // pciMetaData
    );
  }, [formValuesState, primaryLanguageContentsInForm, selectedLanguages]);

  const approversList = useMemo(() => {
    const pastEditors = getPastEditors(campaignView);
    const excludedApprovers = allowSelfApprove ? [] : pastEditors;
    const list = _.map(
      _.filter(newTridents.approvers, (u: any) => !_.includes(excludedApprovers, u.email)),
      (u: any) => ({
        key: u.email,
        text: u.email,
        value: u.email,
      })
    );
    return list;
  }, [newTridents]);

  const onSearchChange = _.debounce((value: string) => {
    if (!value) {
      return;
    }

    // call endpoint to search
    dispatch(
      getApprovers({
        q: value,
        page: 0,
        pageSize: 100,
      }),
    );
  }, 1000);

  return (
    <Flex
      vertical
      gap={10}
      style={{
        margin: 'auto',
        height: '100%',
        width: 844,
        marginTop: 30,
        paddingBottom: 20,
      }}
    >
      <Card title="Approver">
        <Form.Item
          label={
            (isSelfApprove && !allowSelfApprove) ? <Popover content="You are not allowed to send to yourself for approval because you have created or updated the campaign before" placement="top">Approver <MoreInfoOutlined /></Popover> : 'Approver'
          }
          required
          name="approver"
          rules={[{ message: '', required: true }]}
        >
          <Select
            autoClearSearchValue
            showSearch
            style={{ width: '100%' }}
            onSearch={onSearchChange}
            options={approversList}
            placeholder="Search with Grab email"
          />
        </Form.Item>
      </Card>
      <SmartCampaignOverviewCard
        {...formValuesState}
        isPreviewMode
        isManualMode={isCurrentContentSourceManual}
        campaignCode={campaignCode}
        cardTitle="Campaign Settings"
        segment={formValuesState.segments}
        controlGroupTime={controlGroupTime}
        projectID={formValuesState.project}
        channelType={formValuesState.channel}
        selectedLanguages={selectedLanguages}
        primaryLanguage={selectedLanguages[0]}
        metaDataForEachLanguages={metaDataForEachLanguages}
        isRecurrenceEnabled={isRecurrenceEnabled}
        recurrenceWindowDays={recurrenceWindowDays}
        hoursInUTC={formValuesState.hoursInUTC}
        audienceType={formValuesState.audienceType}
      />
    </Flex>
  );
};

/**
 * Get past editors of the campaign
 * @param {Object} campaignView - The campaign view object
 * @returns {Array} - An array of past editors
 */
const getPastEditors = (campaignView: any) => {
  const records = _.get(campaignView, 'edits', []);
  const editors = new Set();
  _.map(records, (r: any) => {
    const changeType = _.get(r, 'change.type');
    if (changeType === 'new' || changeType === 'new') {
      editors.add(r.editor);
    }
  });
  return Array.from(editors);
};

/**
 *
 * @returns Selected Only Variants for each languages
 *          eg:  [languageName]:{contentVariants: [ {  variantNumber, title, message, isControlGroup }, ...], pciMetaData: {...}}
 */
const getContentVariantsAndPCIDataForEachLanguages = (
  primaryLanguageContentsInForm: any,
  formDataForContentVariants: any,
  selectedLanguages: string[],
  pciMetaData: any,
) => {
  const metaDataForEachLanguages: any = {};

  if (!formDataForContentVariants) {
    return metaDataForEachLanguages;
  }

  const roundNames = Object.keys(primaryLanguageContentsInForm.rounds);

  roundNames.forEach((roundName) => {
    const primaryLanguageContentsForCurrentRound =
      primaryLanguageContentsInForm.rounds[roundName];

    const variantNames = Object.keys(primaryLanguageContentsForCurrentRound);

    variantNames.forEach((variantName) => {
      const isControlGroup =
        variantName ===
        primaryLanguageContentsInForm.selectedControlGroupVariant;
      const isChosen =
        primaryLanguageContentsForCurrentRound[variantName].isChosen;

      if (!isChosen && !isControlGroup) {
        return;
      }

      selectedLanguages.forEach((language) => {
        const formDataForCurrentLanguage =
          formDataForContentVariants[roundName][language] || {};

        const formDataForCurrentVariant =
          formDataForCurrentLanguage[variantName] || {};

        const formattedDetailsOfVariant = {
          title:
            formDataForCurrentVariant[
              SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE
            ] || '-',
          message:
            formDataForCurrentVariant[
              SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE
            ] || '-',
          variantNumber: getVariantNumberFromSmartCommsVariantName(variantName),
          slang:
            formDataForCurrentVariant[
              SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_SLANG
            ],
          isControlGroup,
        };

        if (!metaDataForEachLanguages[language]) {
          metaDataForEachLanguages[language] = {};
        }

        const pciData = {
          title: pciMetaData.pciTitle && pciMetaData.pciTitle[language],
          category: pciMetaData.pciCategory && pciMetaData.pciCategory,
          subtitle:
            pciMetaData.pciSubtitle && pciMetaData.pciSubtitle[language],
          coverImageURL:
            pciMetaData.coverImageURL && pciMetaData.coverImageURL[language],
          message: pciMetaData.pciMessage && pciMetaData.pciMessage[language],
          buttonText:
            pciMetaData.pciButtonText && pciMetaData.pciButtonText[language],
          buttonLink:
            pciMetaData.pciButtonLink && pciMetaData.pciButtonLink[language],
        };
        const previousContentVariants =
          metaDataForEachLanguages[language].contentVariants || [];

        metaDataForEachLanguages[language] = {
          pciMetaData: pciData,
          contentVariants: [
            ...previousContentVariants,
            formattedDetailsOfVariant,
          ],
        };
      });
    });
  });

  return metaDataForEachLanguages;
};

export default SmartCampaignPreview;
